import { useRef, useState, useCallback, useEffect } from "react";
import { Upload, X } from "lucide-react";
import { cn } from "@/lib/utils";

interface LocalImageDropZoneProps {
  files: File[];
  onChange: (files: File[]) => void;
}

/**
 * A drop zone that queues images locally (no upload yet).
 * Used in create dialogs where there's no entity ID until after save.
 */
export function LocalImageDropZone({ files, onChange }: LocalImageDropZoneProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);
  const dragOverZoneRef = useRef(false);
  const [isDraggingOver, setIsDraggingOver] = useState(false);

  const addFiles = useCallback(
    (incoming: File[]) => {
      const images = incoming.filter((f) => f.type.startsWith("image/"));
      onChange([...files, ...images]);
    },
    [files, onChange]
  );

  const removeFile = (index: number) => {
    onChange(files.filter((_, i) => i !== index));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    addFiles(Array.from(e.target.files || []));
    e.target.value = "";
  };

  useEffect(() => {
    const el = dropZoneRef.current;
    if (!el) return;

    const isOver = (e: DragEvent) => {
      const r = el.getBoundingClientRect();
      return e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
    };

    const onDragOver = (e: DragEvent) => {
      e.preventDefault();
      const over = isOver(e);
      dragOverZoneRef.current = over;
      if (over) {
        if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
        setIsDraggingOver(true);
      } else {
        setIsDraggingOver(false);
      }
    };

    const onDrop = (e: DragEvent) => {
      e.preventDefault();
      const wasOver = dragOverZoneRef.current;
      dragOverZoneRef.current = false;
      setIsDraggingOver(false);
      if (!wasOver) return;
      const dt = e.dataTransfer;
      const files: File[] = dt?.items
        ? Array.from(dt.items).filter(i => i.kind === "file").map(i => i.getAsFile()).filter((f): f is File => f !== null)
        : dt?.files ? Array.from(dt.files) : [];
      if (files.length) addFiles(files);
    };

    const onDragLeave = (e: DragEvent) => {
      if (!e.relatedTarget) setIsDraggingOver(false);
    };

    document.addEventListener("dragover", onDragOver);
    document.addEventListener("drop", onDrop);
    document.addEventListener("dragleave", onDragLeave);
    return () => {
      document.removeEventListener("dragover", onDragOver);
      document.removeEventListener("drop", onDrop);
      document.removeEventListener("dragleave", onDragLeave);
    };
  }, [addFiles]);

  return (
    <div className="space-y-2">
      {/* Previews of queued files */}
      {files.length > 0 && (
        <div className="grid grid-cols-4 gap-2">
          {files.map((file, i) => (
            <div
              key={i}
              className="relative aspect-square rounded-md overflow-hidden border border-border bg-muted"
            >
              <img
                src={URL.createObjectURL(file)}
                alt={file.name}
                className="w-full h-full object-cover"
              />
              <button
                type="button"
                onClick={() => removeFile(i)}
                className="absolute top-0.5 right-0.5 bg-black/60 hover:bg-black/80 text-white rounded-full p-0.5"
              >
                <X className="h-3 w-3" />
              </button>
              {i === 0 && (
                <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-[9px] text-center py-0.5">
                  main
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Drop zone */}
      <div
        ref={dropZoneRef}
        onClick={() => fileInputRef.current?.click()}
        className={cn(
          "flex flex-col items-center justify-center gap-1.5 rounded-md border-2 border-dashed py-5 transition-colors cursor-pointer select-none",
          isDraggingOver
            ? "border-primary bg-primary/5 text-primary"
            : "border-border text-muted-foreground hover:border-primary/50 hover:bg-muted/30"
        )}
      >
        <Upload className="h-4 w-4" />
        <p className="text-xs">
          {isDraggingOver
            ? "Drop to add"
            : files.length === 0
            ? "Drag & drop photos, or click to browse"
            : "Add more photos"}
        </p>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={handleFileChange}
      />
    </div>
  );
}
